// The widget's "my tickets" reads plus QR generation, called with a
// user-scoped Firebase token (see useTixologiTokenExchange /
// TixologiTokenScope.User in @punchup/data). Responses are Tixologi-owned
// snake_case payloads — typed `unknown` on purpose: hand-writing Tixologi's
// shapes here would drift.

function ticketingApiBaseUrl(): string {
  const baseUrl = process.env.NEXT_PUBLIC_TIXOLOGI_API_URL;
  if (!baseUrl) {
    throw new Error("NEXT_PUBLIC_TIXOLOGI_API_URL not configured");
  }
  return baseUrl;
}

/**
 * The user's tickets summarized per event, across every partner — the list
 * view the widget's MyTickets shows before drilling into one event
 * (`GET /events/my-tickets/summary`). No `partner_id`; identity comes from the
 * token's `userId` claim. Needs the ticketing-api route added in
 * Tixologi-Inc/ticketing-api-2.0#853. Each event carries its `id`, which
 * feeds `getMyEventTickets` for the detail view.
 */
export async function getMyTicketsSummary(params: {
  token: string;
}): Promise<unknown> {
  const { token } = params;

  try {
    const response = await fetch(
      `${ticketingApiBaseUrl()}/events/my-tickets/summary`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Error fetching my tickets summary: ${response.status} - ${errorText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching my tickets summary:", error);
    throw error;
  }
}

/**
 * All of the user's tickets (flat, not grouped by event) across every event
 * and partner, identity resolved from the token's `userId` claim —
 * `GET /events/my-tickets/all`. Unlike the legacy `GET /events/my-tickets`,
 * this takes no `partner_id`; it needs the ticketing-api route added in
 * Tixologi-Inc/ticketing-api-2.0#852.
 */
export async function getMyTickets(params: {
  token: string;
}): Promise<unknown> {
  const { token } = params;

  try {
    const response = await fetch(
      `${ticketingApiBaseUrl()}/events/my-tickets/all`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Error fetching my tickets: ${response.status} - ${errorText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching my tickets:", error);
    throw error;
  }
}

/**
 * The user's tickets for one event — the same call the widget's
 * MyEventTickets view makes (`GET /events/{event_id}/my-tickets`).
 */
export async function getMyEventTickets(params: {
  eventId: string;
  token: string;
}): Promise<unknown> {
  const { eventId, token } = params;

  try {
    const response = await fetch(
      `${ticketingApiBaseUrl()}/events/${encodeURIComponent(eventId)}/my-tickets`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Error fetching my event tickets: ${response.status} - ${errorText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error("Error fetching my event tickets:", error);
    throw error;
  }
}

/**
 * The scannable QR payload for one ticket — the same call the widget makes to
 * render a ticket's QR (`POST /tickets/qr_data`, body `{ ticket_id }`).
 * Returns `{ user_id, code, ticketing_mode }`; `code` is what a scanner
 * validates (dynamic-mode tickets rotate it, static ones don't).
 */
export async function generateTicketQrData(params: {
  ticketId: string;
  token: string;
}): Promise<unknown> {
  const { ticketId, token } = params;

  try {
    const response = await fetch(`${ticketingApiBaseUrl()}/tickets/qr_data`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ticket_id: ticketId }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Error generating ticket QR data: ${response.status} - ${errorText}`
      );
    }

    return response.json();
  } catch (error) {
    console.error("Error generating ticket QR data:", error);
    throw error;
  }
}
